import {useReducer} from 'react';

import {findIndex} from 'lodash';

import legacyCarePlanAPI from '../../api/legacy_care_plan';
import patientVisitAPI from '../../api/patientvisit';
import problemsAPI from '../../api/problems';
import {
    SET_IS_LOADING,
    SET_WINDOW_PROPS,
    GET_PATIENT_VISIT,
    TOGGLE_IS_VALID_PLAN,
    SET_SHOW_PROBLEM_FORM,
    SET_PROBLEM_EDIT,
    EDIT_PROBLEM,
    ADD_PROBLEM,
    UPDATE_PATIENT_VISIT_DATA,
} from '../types';

import {VisitContext, VisitDispatchContext} from './VisitContext';
import VisitReducer from './VisitReducer';

const PATIENT_VISIT_ADDITIONAL_FIELDS =
    '?additional_fields=encounters&additional_fields=additional_user_info';

const VisitState = ({children}) => {
    const initialState = {
        carePlanMetadata: {},
        csrfToken: '',
        currentMedicationsWindowArray: [],
        deficitsWindowArray: [],
        diagnosesWindowArray: [],
        isEditable: false,
        isLoading: true,
        isReadOnly: true,
        isValidPlan: false,
        medicationWindowArray: [],
        patientVisitData: {},
        patientVisitId: null,
        patientVisitType: '',
        patientVisitUsername: '',
        problemsWindowArray: [],
        providers: window.initReactProps ? window.initReactProps.providers : [],
        risk: window.initReactProps ? window.initReactProps.risk : {},
        showProblemForm: false,
        status: null,
        user: {},
    };
    const [state, dispatch] = useReducer(VisitReducer, initialState);
    const setLoading = () => dispatch({type: SET_IS_LOADING});

    const getPatientVisit = async updatedData => {
        setLoading();

        if (!updatedData) {
            try {
                const p = await Promise.all([
                    patientVisitAPI.get(
                        `/${window.initReactProps.patientVisitId}/${PATIENT_VISIT_ADDITIONAL_FIELDS}`,
                    ),
                    legacyCarePlanAPI.get(
                        `/${window.initReactProps.organization.superOrganizationId}/patients/${window.initReactProps.patientVisitUsername}/legacy/care-plans/`,
                    ),
                ]);

                updatedData = p.reduce(
                    (acc, cur) => {
                        if (cur.data.data) {
                            return Object.assign({}, acc, {
                                data: Object.assign(
                                    {},
                                    acc.data,
                                    cur.data.data,
                                ),
                                meta: Object.assign(
                                    {},
                                    acc.meta,
                                    cur.data.meta,
                                ),
                            });
                        }

                        return Object.assign({}, acc, {
                            data: Object.assign({}, acc.data, cur.data),
                            meta: Object.assign({}, acc.meta, cur.meta),
                        });
                    },
                    {data: {}, meta: {}},
                );
            } catch (err) {
                console.error(err);
                updatedData = null;
            }
        }

        checkIfValidPlan(updatedData.data?.problem_set);

        dispatch({
            payload: updatedData,
            type: GET_PATIENT_VISIT,
        });
    };

    const addProblem = async problem => {
        try {
            const res = await problemsAPI.post('/', problem);
            dispatch({
                payload: res.data,
                type: ADD_PROBLEM,
            });
        } catch (err) {
            console.error(err);
        }
    };

    const editProblem = async problem => {
        try {
            await problemsAPI.patch(`/${state.problemToEdit.id}/`, problem);
            getPatientVisit();
            dispatch({
                type: EDIT_PROBLEM,
            });
        } catch (err) {
            console.error(err);
        }
    };

    const setProblemToEdit = problem => {
        setLoading();
        setShowProblemForm(true);
        if (!problem) {
            problem = null;
        }
        dispatch({
            payload: problem,
            type: SET_PROBLEM_EDIT,
        });
    };

    const setShowProblemForm = isShowing => {
        dispatch({
            payload: isShowing,
            type: SET_SHOW_PROBLEM_FORM,
        });
    };

    const setWindowProps = () => {
        const windowProps = window.initReactProps;
        dispatch({
            payload: windowProps,
            type: SET_WINDOW_PROPS,
        });
    };

    const updatePatientVisitEncounterState = (
        patientVisitData,
        encounterId,
        attachmentObject,
        status,
    ) => {
        const updatedPatientVisitData = {...patientVisitData};

        const encounterIndex = findIndex(
            updatedPatientVisitData.encounters,
            enc => `${enc.id}` === `${encounterId}`,
        );

        if (encounterIndex > -1) {
            updatedPatientVisitData.encounters[
                encounterIndex
            ].encounter_note.attachment_object = attachmentObject;
            updatedPatientVisitData.encounters[
                encounterIndex
            ].encounter_note.status = status;

            dispatch({
                payload: updatedPatientVisitData,
                type: UPDATE_PATIENT_VISIT_DATA,
            });
        }
    };

    const checkIfValidPlan = (problem_set = []) => {
        const hasEmptyGoalSet = problem_set.filter(problem => {
            return problem.goal_set?.length < 1;
        });
        if (hasEmptyGoalSet.length) {
            dispatch({
                payload: false,
                type: TOGGLE_IS_VALID_PLAN,
            });
        } else {
            const hasEmptyInterventionSet = problem_set.filter(problem => {
                let emptySet = problem.goal_set?.filter(goal => {
                    return (
                        goal.status !== 'discontinued' &&
                        goal.intervention_set?.length < 1
                    );
                });
                if (emptySet.length) {
                    return emptySet;
                } else {
                    return null;
                }
            });
            if (hasEmptyInterventionSet.length) {
                dispatch({
                    payload: false,
                    type: TOGGLE_IS_VALID_PLAN,
                });
            } else {
                dispatch({
                    payload: true,
                    type: TOGGLE_IS_VALID_PLAN,
                });
            }
        }
    };

    return (
        <VisitContext.Provider
            value={{
                addProblem,
                carePlanMetadata: state.carePlanMetadata,
                checkIfValidPlan,
                csrfToken: state.csrfToken,
                currentMedicationsWindowArray:
                    state.currentMedicationsWindowArray,
                deficitsWindowArray: state.deficitsWindowArray,
                diagnosesWindowArray: state.diagnosesWindowArray,
                editProblem,
                getPatientVisit,
                isEditable: state.isEditable,
                isLoading: state.isLoading,
                isReadOnly: state.isReadOnly,
                isValidPlan: state.isValidPlan,
                medicationWindowArray: state.medicationWindowArray,
                patientVisitData: state.patientVisitData,
                patientVisitId: state.patientVisitId,
                patientVisitType: state.patientVisitType,
                patientVisitUsername: state.patientVisitUsername,
                problemToEdit: state.problemToEdit,
                problemsWindowArray: state.problemsWindowArray,
                providers: state.providers,
                risk: state.risk,
                setProblemToEdit,
                setShowProblemForm,
                setWindowProps,
                showProblemForm: state.showProblemForm,
                status: state.status,
                updatePatientVisitEncounterState,
                user: state.user,
            }}
        >
            <VisitDispatchContext.Provider value={dispatch}>
                {children}
            </VisitDispatchContext.Provider>
        </VisitContext.Provider>
    );
};

export default VisitState;
